<template>
  <MobileContainer>
    <MobileHeader slot="header">
      Burger Boss - {{ store.city }}
    </MobileHeader>

    <div :class="$style.locationCard">
      <GmapMap
        :class="$style.gMap"
        :center.sync="center"
        :zoom="zoom"
        map-type-id="terrain"
      >

        <GmapMarker
          :clickable="true"
          :draggable="false"
          :icon="icon"
          :position="restMarker"
          title="restaurant"/>
      </GmapMap>
      <MobileButton :class="$style.underMapButton"
                    @click="locationClickHandler()"
      >
        <svg v-svg
             :class="$style.btnIcon"
             symbol="burger-white"
             height="20px"
             width="29px"
        />
        Order from this location
      </MobileButton>

      <div :class="$style.infoCard">
        <svg v-svg
             :class="$style.infoCardIcon"
             symbol="pin-green"
             height="21px"
             width="17px"
        />
        <div :class="$style.infoCardContent">
          <h5 :class="$style.infoCardTitle">Address</h5>
          <p :class="$style.infoCardText">{{ store.address }}</p>
        </div>
        <MobileButton
          :class="$style.infoCardBtn"
          btn-style="round"
          @click="openMap"
        >
          Map
        </MobileButton>
      </div>

      <hr :class="$style.cardDelimiter" >

      <div :class="$style.infoCard">
        <svg v-svg
             :class="$style.infoCardIcon"
             symbol="phone-green"
             height="19px"
             width="18px"
        />
        <div :class="$style.infoCardContent">
          <h5 :class="$style.infoCardTitle">Phone</h5>
          <p :class="$style.infoCardText">{{ formatPhone(store.phone) }}</p>
        </div>
        <MobileButton
          :class="$style.infoCardBtn"
          :href="'tel:' + store.phone"
          btn-style="round"
        >
          Call
        </MobileButton>
      </div>

      <hr :class="$style.cardDelimiter" >

      <div :class="$style.infoCard">
        <svg v-svg
             :class="$style.infoCardIcon"
             symbol="clock-green"
             height="18px"
             width="18px"
        />
        <div :class="$style.infoCardContent">
          <h5 :class="$style.infoCardTitle">Store hours</h5>
          <dd :class="$style.infoCardText">{{ store.storeHours }}</dd>
        </div>
      </div>

    </div>

    <MobileModal v-model="showCancelOrderConfirm"
                 dialog
                 center-content>
      <MobileConfirmDialog title="Cancel Order"
                           cancel-text="No"
                           confirm-text="Yes, cancel"
                           @confirm="confirmCancelOrder()"
                           @cancel="rejectCancelOrder()">
        Are you sure you want to cancel your entire order?
      </MobileConfirmDialog>
    </MobileModal>
  </MobileContainer>
</template>

<script>
import Component from 'vue-class-component';
import { Getter, Action } from 'vuex-class';
import Vue from 'vue';
import markerImg from '@/assets/img/map_icon.png';
import BaseButton from '@/components/BaseButton';
import utils from '@/lib/helpers';
import MobileContainer from '@/apps/mobile/components/MobileContainer';
import MobileHeader from '@/apps/mobile/components/MobileHeader';
import MobileButton from '@/apps/mobile/components/MobileButton';
import MobileModal from '@/apps/mobile/components/MobileModal';
import MobileConfirmDialog from '@/apps/mobile/components/MobileConfirmDialog';
import { ORDER_SET_RESTAURANT, ORDER_SET_TYPE, CART_RESET } from '@/apps/cart/store/types';
import { ORDER_TYPES } from '@/config/constants';
/**
 * Page to check the location.
 */
@Component({
  components: {
    MobileButton,
    MobileHeader,
    MobileContainer,
    BaseButton,
    MobileModal,
    MobileConfirmDialog,
  },
})
export default class MobileLocation extends Vue {
  @Getter rests;
  @Getter restByKey;
  @Getter isCordovaReady;
  @Getter orderItemCompleted;
  @Getter order;
  @Action(ORDER_SET_RESTAURANT) setOrderRest;
  @Action(ORDER_SET_TYPE) setOrderType;
  @Getter('authUser') user;
  @Action(CART_RESET) clearCart;

  store = {};
  center = {
    lat: 0,
    lng: 0,
  };
  restMarker = {
    lat: 0,
    lng: 0,
  };
  zoom = 18;
  icon = { url: markerImg };
  formatPhone = utils.formatPhoneDots;
  showCancelOrderConfirm = false;

  /**
   * Get current restaurant and define coordinates for map
   */
  created() {
    const restaurant = this.restByKey(this.$route.params.restId);

    if (restaurant) {
      this.store = restaurant;
      this.restMarker.lat = this.store.location.latitude;
      this.restMarker.lng = this.store.location.longitude;

      this.center = this.restMarker;
    }
  }

  /**
   * success callback
   */
  onSuccess(result) {
    console.log(`Success:${result}`);
  }

  /**
   * error callback
   */
  onError(result) {
    console.log(`Error:${result}`);
  }

  /**
   * Handle click on location change button.
   */
  locationClickHandler() {
    if (this.order && this.orderItemCompleted) {
      this.showCancelOrderConfirm = true;

      return;
    }

    this.goToMenu();
  }

  /**
   * Go to menu with current restaurant id.
   */
  async goToMenu() {
    await this.setOrderRest(this.store.id);

    const possibleOptions = {
      curbside: this.store.isCurbsideSupported,
      pickup:   this.store.isPickupSupported,
    };

    if (possibleOptions.curbside && !possibleOptions.pickup) {
      this.setOrderType(ORDER_TYPES.curbside);
    } else if (possibleOptions.pickup && !possibleOptions.curbside) {
      this.setOrderType(ORDER_TYPES.pickup);
    }

    const nextRouteName = this.user
      ? 'menu'
      : 'join-sign-in';

    this.$router.push({
      name:   nextRouteName,
      params: { restaurantId: this.store.id },
    });
  }

  /**
   * Open standard Directions app
   */
  openMap() {
    if (this.isCordovaReady === false) {
      console.log('It is not mobile application');

      return;
    }

    const lat = this.store.location.latitude;
    const lng = this.store.location.longitude;
    const label = encodeURI(`${this.store.address} Burger Boss`);

    /* global device */
    if (device.platform.toLowerCase() === 'android') {
      window.open(`geo:${lat},${lng}?q=${label}`, '_system');
    }

    if (device.platform.toLowerCase() === 'ios') {
      window.open(`comgooglemaps://?q=${label}&center=${lat},${lng}&zoom=15`, '_system');
    }
  }

  /**
   * Confirm cancel order.
   */
  confirmCancelOrder() {
    this.clearCart();
    this.showCancelOrderConfirm = false;
    this.goToMenu();
  }

  /**
   * Reject cancel order.
   */
  rejectCancelOrder() {
    this.showCancelOrderConfirm = false;
  }
}
</script>

<style lang="scss" module>
@import "~@/style/variables.scss";
@import "~@/style/helpers.scss";

.locationCard {
  height: 100%;
  background: $mobile-locations-list-bg;
}

.gMap {
  height: 30vh;
  min-height: 174px;
  width: 100%;
}

.underMapButton {
  margin: 0;

  .btnIcon {
    margin-right: 15px;
  }
}

.infoCard {
  color: $black;
  font-family: $main-font-family;
  padding: 10px $mobile-content-side-padding 15px;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &Icon {
    width: 18px;
    align-self: self-start;
    margin-right: 15px;
    flex-shrink: 0;
  }

  &Content {
    flex: auto;
  }

  &Title {
    font-family: $main-font-family;
    font-weight: 700;
    font-size: 1rem;
    letter-spacing: -0.21px;
  }

  &Text {
    font-size: 1rem;
    font-weight: 300;
    letter-spacing: -0.21px;
  }

  &Btn {
    margin: 0 0 0 10%;
  }

  &DlList {
    display: flex;
    flex-wrap: wrap;

    dt,
    dd {
      flex: 1 1 50%;
      align-items: center;
    }

    dd {
      text-align: right;
    }
  }
}

.cardDelimiter {
  box-sizing: border-box;
  height: 1px;
  width: 100%;
  background: $mobile-black-light-color;
}

</style>



// WEBPACK FOOTER //
// src/apps/mobile/components/MobileLocation.vue